import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CircleMarker, Marker, Popup, Tooltip } from 'react-leaflet';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { DEFAULT_POLL_INTERVAL_MS } from '../../../hooks/useDriverLocationsPoll';
import {
  formatClockTime,
  formatMiles,
  pluralize,
} from '../../../utils/displayFormat';
import { stopDisplayState } from '../../../utils/composeDriverRecords';
import legendStyles from './legendStyles';
import mapOverlayCard from './mapOverlayStyles';
import { buildTrailLayers } from './trailLayers';
import { trailDistanceMiles } from './trailGeometry';
import { makeRouteStopIcon } from './mapIcons';
import { hasCoords } from './routePaths';
import MonitoringMapShell from './MonitoringMapShell';
import TrailHoverCard from './TrailHoverCard';
import StopPopupContent from './StopPopupContent';
import PollCountdown from './PollCountdown';
import CollapsibleSidePanel from './CollapsibleSidePanel';
import DriverDayStops, { driverDayShape } from './DriverDayStops';

// Driver day view — the Map toggle's only mode. A person, not a route: pick
// a driver and the map draws every position they reported that day, start
// to now, with a disc at each stop they serviced whatever route it belonged
// to, numbered in the order they were visited. Beside it, the same stops as
// a list. Until a driver is picked the pane is a prompt pointing at the
// picker — the map has nothing to draw before then.

const PANEL_STORAGE_KEY = 'monitoring:driver-day-panel-open';

const StyledDiv = styledComponent('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: theme.spacing.sectionGap,
  alignItems: 'stretch',

  '& .ddp-map-card': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    minHeight: theme.sizes.mapPaneMinHeight,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  '& .ddp-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.gapXl,
    padding: theme.spacing.cellPadding,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .ddp-title': {
    fontSize: theme.sizes.subheadFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .ddp-subtitle': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontVariantNumeric: 'tabular-nums',
    marginTop: theme.spacing.gapXs,
  },
  ...legendStyles('ddp'),
  '& .ddp-map-wrap': {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  // Top-right, clear of Leaflet's zoom control (top-left) and the Recenter
  // button (bottom-right).
  '& .ddp-overlay': {
    ...mapOverlayCard(),
    top: theme.spacing.gapXl,
    right: theme.spacing.gapXl,
    padding: theme.spacing.paddingSm,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .ddp-overlay-value': {
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.semibold,
  },
  // Shown until a driver is picked, or when nothing is recorded for them.
  '& .ddp-prompt': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing.gapMd,
    padding: theme.spacing.cardPadding,
    minHeight: theme.sizes.mapPaneMinHeight,
  },
  '& .ddp-prompt-title': {
    fontSize: theme.sizes.headingFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .ddp-prompt-sub': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    maxWidth: theme.sizes.maxWidthSm,
    lineHeight: 1.5,
  },
});

function Prompt({ title, children }) {
  return (
    <StyledDiv>
      <div className="ddp-map-card">
        <div className="ddp-prompt" role="status">
          <div className="ddp-prompt-title">{title}</div>
          <div className="ddp-prompt-sub">{children}</div>
        </div>
      </div>
    </StyledDiv>
  );
}

Prompt.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function DriverDayPane({
  day,
  trail,
  selectedDriverEmail,
  isLoading,
  lastPolledAt,
  pollIntervalMs,
  fitKey,
  onOpenRoute,
}) {
  // The recorded point under the cursor, for the inspection card.
  const [hoverPoint, setHoverPoint] = useState(null);

  // The person's own trail: one line, whatever routes it crossed.
  const trailLayers = useMemo(
    () =>
      day
        ? buildTrailLayers({ routeId: `driver-${day.email}` }, trail, {
            onTrailHover: setHoverPoint,
          })
        : null,
    [day, trail],
  );

  // One disc per stop, numbered in visit order, with the same popup the
  // route map opens so a stop reads identically wherever it is clicked.
  const stopMarkers = useMemo(
    () =>
      day
        ? day.stops.filter(hasCoords).map((stop, index) => (
            <Marker
              key={`${stop.routeId}-${stop.stopId}`}
              position={[stop.latitude, stop.longitude]}
              icon={makeRouteStopIcon({
                label: index + 1,
                fill: stopDisplayState(stop),
                pulse: stop.state === 'current',
              })}
            >
              <Popup>
                <StopPopupContent
                  stop={stop}
                  driver={{
                    driverNames: [day.name],
                    routeName: stop.routeName,
                    stopsTotal: stop.routeStopsTotal,
                  }}
                />
              </Popup>
            </Marker>
          ))
        : [],
    [day],
  );

  // The camera frames the driver's readings and stops, not a route.
  const fitPoints = useMemo(
    () => (day ? [...trail, ...day.stops] : []),
    [day, trail],
  );

  if (!selectedDriverEmail) {
    return (
      <Prompt title="Pick a driver to see their day">
        Use the <strong>Find driver</strong> search above. You&apos;ll see
        everywhere they drove and every stop they serviced, across every route
        they worked. Pick <strong>Show all drivers</strong> to see the whole
        fleet on one map.
      </Prompt>
    );
  }
  if (!day) {
    return (
      <Prompt title="Nothing recorded for this driver yet">
        No scans, stockings or drive legs have been recorded for them today.
      </Prompt>
    );
  }

  const milesDriven = formatMiles(trailDistanceMiles(trail));
  const subtitleParts = [
    pluralize(day.stopsServiced, 'stop'),
    pluralize(day.routes.length, 'route'),
    milesDriven ? `${milesDriven} driven` : null,
    lastPolledAt
      ? `Updated ${formatClockTime(lastPolledAt, { hour: '2-digit' })}`
      : null,
  ].filter(Boolean);

  return (
    <StyledDiv>
      <div className="ddp-map-card">
        <div className="ddp-header">
          <div>
            <div className="ddp-title">{day.name || day.email}</div>
            <div className="ddp-subtitle">{subtitleParts.join(' · ')}</div>
          </div>
          <PollCountdown
            lastPolledAt={lastPolledAt}
            intervalMs={pollIntervalMs}
            isLoading={isLoading}
          />
        </div>
        <div className="ddp-legend" aria-label="Map legend" role="group">
          <span className="ddp-legend-item">
            <span className="ddp-swatch-line" /> Driven
          </span>
          <span className="ddp-legend-item">
            <span className="ddp-swatch-ring" /> Day start
          </span>
          <span className="ddp-legend-item">
            <span className="ddp-swatch-line faint" />
            <span className="ddp-swatch-diamond" /> GPS gap
          </span>
          <span className="ddp-legend-item">
            <span className="ddp-swatch-dot stocked" /> Stocked
          </span>
          <span className="ddp-legend-item">
            <span className="ddp-swatch-dot pulse" /> Current stop
          </span>
        </div>
        <div className="ddp-map-wrap">
          <div className="ddp-overlay" aria-live="polite">
            <span className="ddp-overlay-value">{day.stopsServiced}</span>
            {' serviced'}
          </div>
          <MonitoringMapShell fitPoints={fitPoints} fitKey={fitKey}>
            {trailLayers.trailPolylines}
            {trailLayers.gapPolylines}
            {trailLayers.trailDots}
            {trailLayers.gapMarkers}
            {trailLayers.startMarker}
            {stopMarkers}
            {hoverPoint ? (
              <CircleMarker
                center={[hoverPoint.latitude, hoverPoint.longitude]}
                radius={6}
                color={theme.colors.white}
                weight={2}
                fillColor={theme.colors.routeLine}
                fillOpacity={1}
                interactive={false}
              >
                <Tooltip permanent direction="top" offset={[0, -8]}>
                  <TrailHoverCard
                    driver={{ driverName: day.name, driverEmail: day.email }}
                    point={hoverPoint}
                  />
                </Tooltip>
              </CircleMarker>
            ) : null}
          </MonitoringMapShell>
        </div>
      </div>
      <CollapsibleSidePanel
        title="Stops"
        count={day.stops.length}
        storageKey={PANEL_STORAGE_KEY}
      >
        <DriverDayStops day={day} onOpenRoute={onOpenRoute} />
      </CollapsibleSidePanel>
    </StyledDiv>
  );
}

DriverDayPane.propTypes = {
  // Null until a driver is picked, or when nothing is recorded for them.
  day: driverDayShape,
  // Every reading this driver took today, route-agnostic, oldest first.
  trail: PropTypes.arrayOf(PropTypes.object),
  selectedDriverEmail: PropTypes.string,
  isLoading: PropTypes.bool,
  lastPolledAt: PropTypes.instanceOf(Date),
  pollIntervalMs: PropTypes.number,
  fitKey: PropTypes.string.isRequired,
  // (routeId, stopId?) — open that route's map, focused on the stop if given.
  onOpenRoute: PropTypes.func.isRequired,
};

DriverDayPane.defaultProps = {
  day: null,
  trail: [],
  selectedDriverEmail: null,
  isLoading: false,
  lastPolledAt: null,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
};

export default DriverDayPane;
